import { getDatabase, ref, get } from "firebase/database";
import { getStartOfMonth } from "@/scripts/dateUtils.js";
import { pricingStore } from "@/store/pricing.js";

// Pooled usage counts matching the server's counting semantics
// (helpers/dialog.py): every language subtree, incomplete dialogs included.
// Exported so it can be exercised directly rather than only through a fetch.
export const countDialogs = (snapshot, sinceTimestamp) => {
  const usage = { chats: 0, lessons: 0 };
  if (!snapshot.exists()) return usage;
  snapshot.forEach((langNode) => {
    langNode.forEach((dialogNode) => {
      const d = dialogNode.val();
      if (!d?.startedAt || d.startedAt < sinceTimestamp) return;
      if (d.type === "FREESTYLE_CHAT") usage.chats++;
      else if (d.type === "LESSON") usage.lessons++;
    });
  });
  return usage;
};

const yearMonthIn = (timeZone) => {
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone,
    year: "numeric",
    month: "numeric",
  }).formatToParts(new Date());
  return {
    year: Number(parts.find((p) => p.type === "year").value),
    month: Number(parts.find((p) => p.type === "month").value),
  };
};

// Current calendar year/month as the given IANA timezone sees them; the
// device timezone when none is given or Intl rejects the id.
export function getCurrentYearMonthInTimezone(timeZone) {
  if (timeZone) {
    try {
      return yearMonthIn(timeZone);
    } catch (error) {
      console.error("Falling back to device timezone:", error);
    }
  }
  const now = new Date();
  return { year: now.getFullYear(), month: now.getMonth() + 1 };
}

// Epoch seconds of the 1st at 00:00 in the given IANA timezone, mirroring the
// server's month boundary (helpers/dialog.countDialogsThisMonth computes it in
// users/{uid}/timezone). Without a timezone — or with an id Intl rejects — the
// device timezone is used, matching the pre-multi-language behavior.
// Date components come from Intl.formatToParts throughout: re-parsing
// toLocaleString output with new Date() is implementation-defined.
export function getStartOfMonthInTimezone(timeZone) {
  if (!timeZone) return getStartOfMonth();
  try {
    const { year, month } = yearMonthIn(timeZone);
    // Epoch of Y-M-01T00:00 in the zone: take the UTC instant with that wall
    // time and subtract the zone's UTC offset at that instant.
    const guess = Date.UTC(year, month - 1, 1);
    const parts = {};
    const fields = new Intl.DateTimeFormat("en-US", {
      timeZone,
      year: "numeric",
      month: "numeric",
      day: "numeric",
      hour: "numeric",
      minute: "numeric",
      second: "numeric",
      hourCycle: "h23",
    }).formatToParts(new Date(guess));
    for (const part of fields) parts[part.type] = part.value;
    const zonedAsUtc = Date.UTC(
      Number(parts.year),
      Number(parts.month) - 1,
      Number(parts.day),
      Number(parts.hour),
      Number(parts.minute),
      Number(parts.second)
    );
    const offsetMs = zonedAsUtc - guess;
    return Math.floor((guess - offsetMs) / 1000);
  } catch (error) {
    console.error("Falling back to device timezone for the usage window:", error);
    return getStartOfMonth();
  }
}

export async function fetchMonthlyUsage(userId, timezone) {
  if (!userId) return { chats: 0, lessons: 0 };
  const snapshot = await get(ref(getDatabase(), `dialogs/${userId}`));
  return countDialogs(snapshot, getStartOfMonthInTimezone(timezone));
}

export async function fetchLifetimeUsage(userId) {
  if (!userId) return { chats: 0, lessons: 0 };
  const snapshot = await get(ref(getDatabase(), `dialogs/${userId}`));
  return countDialogs(snapshot, 0);
}

// ---------------------------------------------------------------------------
// Resumable-session detection.
//
// The limit gates STARTING a session, never RESUMING one, and the server
// agrees: hasReachedChatLimit is called only on the creation paths, and
// continueChat never calls it. continueChat does refuse turns of its own now
// (a per-account rate limit and a per-dialog turn ceiling, both added for the
// RTDB abuse audit's Finding 2), but neither of those reads the plan or the
// chat count, so neither can refuse a resume that the paywall would allow.
// An already-counted incomplete dialog is already paid for by the count, so
// refusing to reopen it grants nothing and strands the user.
//
// A row is incomplete when it has a start and NO completion. `completedAt ==
// null` (absent or null) rather than a truthiness test on purpose: that is
// exactly what the per-language `orderByChild("completedAt").equalTo(null)`
// query this replaces matched, so no row becomes resumable that was not
// resumable before. An anomalous `completedAt: 0` therefore still reads as
// completed, which is the conservative direction for a paywall-relevant field
// living under the client-writable `dialogs/{uid}` subtree.
// ---------------------------------------------------------------------------
const isIncompleteRow = (dialog) =>
  !!dialog?.startedAt && dialog.completedAt == null;

/**
 * The incomplete sessions in a `dialogs/{uid}` snapshot, pooled across every
 * language exactly as countDialogs pools — pooling is simply what the single
 * read contains. Callers that resume into a specific language filter the
 * result themselves; that decision belongs to the view, not here.
 *
 * Deliberately NOT windowed by `startedAt`: a session started before the
 * current month boundary is still open and still resumable.
 *
 * @param {object} snapshot a `dialogs/{uid}` DataSnapshot
 * @param {string} [type] restrict to one dialog type (e.g. "FREESTYLE_CHAT")
 * @returns {Array<{lang: string, dialogId: string, startedAt: number, type: string}>}
 */
export const getIncompleteSessions = (snapshot, type) => {
  const sessions = [];
  if (!snapshot?.exists()) return sessions;
  snapshot.forEach((langNode) => {
    langNode.forEach((dialogNode) => {
      const dialog = dialogNode.val();
      if (!isIncompleteRow(dialog)) return;
      if (type && dialog.type !== type) return;
      sessions.push({
        lang: langNode.key,
        dialogId: dialogNode.key,
        startedAt: dialog.startedAt,
        type: dialog.type,
      });
    });
  });
  return sessions;
};

/**
 * The usage counts AND the resumable sessions, from exactly ONE
 * `dialogs/{uid}` read.
 *
 * Deliberately a separate export rather than a widened `fetchLifetimeUsage`
 * return: every existing limit-wall test mocks the fetch it uses, and a widened
 * shape would make those mocks silently answer "nothing to resume" — the fix
 * would be inert under test and for any caller that failed to thread the new
 * field. A distinct function forces every consumer to wire it explicitly.
 *
 * Callers that render a limit wall MUST take both halves from the same call:
 * two async reads leave a drift window in which the wall can be painted over a
 * user who has a session open.
 *
 * @param {string} userId
 * @param {{sinceTimestamp?: number, type?: string}} [options] `sinceTimestamp`
 *   is the counting window (0 = lifetime, month start = monthly); `type`
 *   restricts detection only — counting always covers both types, unchanged.
 * @returns {Promise<{usage: {chats: number, lessons: number}, incomplete: Array}>}
 */
export async function fetchUsageAndIncomplete(
  userId,
  { sinceTimestamp = 0, type } = {}
) {
  if (!userId) return { usage: { chats: 0, lessons: 0 }, incomplete: [] };
  const snapshot = await get(ref(getDatabase(), `dialogs/${userId}`));
  return {
    usage: countDialogs(snapshot, sinceTimestamp),
    incomplete: getIncompleteSessions(snapshot, type),
  };
}

// ---------------------------------------------------------------------------
// Freebies: the entitlement half of this module.
//
// `users/{uid}/freebies` is a server-owned grant that RAISES the free lifetime
// ceiling; it is not a consumable ledger, so counting above is untouched and
// only the limit moves. On the current 3-chat / 1-lesson base, a 2/2 grant
// means a 5-chat / 3-lesson ceiling, never a spend-down of 2.
//
// This is the single client-side derivation. Every consumer (FreeChatView,
// LessonsView, AccountView, and the U4 explainer modal) reads it here rather
// than parsing the node itself.
//
// It mirrors ONE backend function: helpers/users.getFreebiesBonus, the read
// path that actually decides the entitlement. A client that accepted a shape
// that read path rejects would advertise a bonus the user cannot spend, and one
// that rejected a shape it accepts would hide a bonus the server is honoring.
// jobs/grantLaunchFreebies.matchesThisGrant is deliberately NOT mirrored: it is
// a narrower "did THIS backfill already write this exact node" resume check
// (exact key set, bonuses exactly 2, reason "mobile-launch"), not a statement
// about which nodes are valid. A node the read path honors but the job does not
// recognize is spendable by the user and reported as foreign by the job, which
// is the intended operator signal.
// ---------------------------------------------------------------------------

// Mirrors helpers/users.MAX_BONUS. The node lives under a client-writable
// subtree, so a value beyond this is treated as tampering, not as a grant.
// tests/unit/freebiesEntitlement.test.js parses the Python constant and fails
// if these two drift.
export const MAX_FREEBIES_BONUS = 10;

const FREEBIES_KEYS = { chats: "bonusChats", lessons: "bonusLessons" };

// Own properties only, mirroring Python's dict lookup: an inherited key (a
// polluted Object.prototype, a hand-built object) is invisible to the backend,
// so honoring it here would manufacture a client-only entitlement — or, for an
// inherited malformed value, suppress a real one.
const ownValue = (object, key) =>
  Object.prototype.hasOwnProperty.call(object, key) ? object[key] : undefined;

// Mirrors helpers/users._isBonusValue as closely as JavaScript allows.
// Number.isInteger plus the typeof guard reject bool, NaN, Infinity, strings
// and fractional values, matching the Python.
//
// ONE input cannot be matched, by construction: Python's `_isBonusValue`
// rejects a float, so a node hand-edited in the Firebase console to `2.0`
// reads as bonus 0 on the server, while JS sees a plain 2 (there is no
// int/float distinction in the language) and would show the raised ceiling.
// The user would then be shown a bonus `checkFreeUserLimit` refuses to honor.
// This is unreachable through the grant job, which writes real Python ints,
// and is exactly why its dry run flags any pre-existing node as an anomaly for
// an operator to clear. Do not "fix" it on this side: JS cannot observe the
// distinction, so the only real remedy is not hand-editing the node.
const isBonusValue = (value) =>
  typeof value === "number" &&
  Number.isInteger(value) &&
  value >= 0 &&
  value <= MAX_FREEBIES_BONUS;

/**
 * Bonus lifetime dialogs granted by `users/{uid}/freebies`.
 *
 * Fails closed AS A UNIT, exactly like the backend: if any present bonus value
 * is malformed the whole node is ignored rather than honoring its well-formed
 * half, because a half-garbage node is not trustworthy enough to widen an
 * entitlement. An absent key is simply a zero bonus, not an anomaly.
 *
 * @param {object|null|undefined} userData the `users/{uid}` snapshot value
 * @returns {{chats: number, lessons: number}} offsets, never negative
 */
export function getFreebiesBonus(userData) {
  const none = { chats: 0, lessons: 0 };
  if (userData === null || typeof userData !== "object") return none;
  const freebies = ownValue(userData, "freebies");
  if (freebies === null || freebies === undefined) return none;
  // RTDB collapses a node whose keys are 0..n into a JS array, and a scalar
  // node stays a scalar; both must be rejected the way isinstance(_, dict) is.
  if (typeof freebies !== "object" || Array.isArray(freebies)) return none;
  const values = {};
  for (const [outKey, nodeKey] of Object.entries(FREEBIES_KEYS)) {
    const value = ownValue(freebies, nodeKey);
    if (value !== undefined && value !== null && !isBonusValue(value)) {
      return none;
    }
    values[outKey] = value || 0;
  }
  return { chats: values.chats, lessons: values.lessons };
}

/**
 * The free plan's lifetime ceilings with any grant applied. Every free-tier
 * gate check and every free-tier total in the UI reads this, so the client
 * gate matches `helpers/users.checkFreeUserLimit`.
 *
 * @param {object|null|undefined} userData the `users/{uid}` snapshot value
 * @returns {{chats: number, lessons: number}}
 */
export function getEffectiveFreeLimits(userData) {
  const bonus = getFreebiesBonus(userData);
  return {
    chats: pricingStore.plans.free.lifetimeChats + bonus.chats,
    lessons: pricingStore.plans.free.lifetimeLessons + bonus.lessons,
  };
}

/**
 * Whether this account holds a grant worth telling the user about. Deliberately
 * "a usable bonus survives coercion" rather than "the node exists": a node the
 * server refuses to honor must not light up bonus copy the user cannot spend.
 *
 * @param {object|null|undefined} userData the `users/{uid}` snapshot value
 */
export function hasFreebiesGrant(userData) {
  const bonus = getFreebiesBonus(userData);
  return bonus.chats > 0 || bonus.lessons > 0;
}

/**
 * The timezone the server assumes when `users/{uid}/timezone` is unset.
 *
 * `helpers/users.checkPaidUserLimit` reads `userData.get("timezone", "UTC")`, so
 * an account that has never stored a zone counts its month in UTC server-side.
 * Any client that falls back to the DEVICE zone instead disagrees with the
 * server for exactly the accounts least likely to have a stored zone, which is
 * why both gate views import this constant rather than passing `undefined` (the
 * value `getStartOfMonthInTimezone` reads as "use the device zone").
 */
export const SERVER_DEFAULT_TIMEZONE = "UTC";

/**
 * Whether the SERVER would treat this account as a free-tier user.
 *
 * This is the exact mirror of `helpers/users.isFreeUser`, which reads
 * `"features" not in userData or userData["features"] is None` — the single
 * test that decides, on the backend, whether a dialog-creation attempt is held
 * to the free lifetime ceiling (`checkFreeUserLimit`) or to a monthly allowance
 * (`checkPaidUserLimit`). Both gate views branch on this and nothing else, so a
 * user the server lets through cannot be stopped by the client, and a user the
 * server would stop cannot be waved through.
 *
 * Absent-or-null, NOT falsy, and the difference is the reason this is a shared
 * helper instead of an inline expression in each view. A `features` value of
 * `false`, `0` or `""` is a PAID account to the backend's membership test,
 * while `!userData.features` — which `LessonsView.vue` used before this helper
 * existed — reads all three as free and applies a ceiling of one lifetime
 * lesson to an entitled account. Those values only arise from hand-editing or
 * legacy data, but an admin-granted comp puts a features node on accounts with
 * no billing record at all, so the number of accounts whose entitlement rests
 * entirely on this node is no longer small.
 *
 * Own properties only, for the same reason `getFreebiesBonus` checks them: a
 * key that Python's dict membership test cannot see must not decide this
 * either.
 *
 * @param {object|null|undefined} userData the `users/{uid}` snapshot value
 * @returns {boolean} true when the server would apply the free-tier ceiling
 */
export function isServerFreeUser(userData) {
  const container =
    userData === null || typeof userData !== "object" ? {} : userData;
  const features = ownValue(container, "features");
  return features === undefined || features === null;
}

/**
 * The monthly dialog ceiling for an account `isServerFreeUser` calls paid, or
 * null when none can be resolved.
 *
 * Null is a real answer, not an error. `helpers/users.checkPaidUserLimit` reads
 * `features[key]`, falls back to the plan behind the billing lookup key, and
 * returns False -- do not block -- when neither yields a number. A client that
 * substitutes the free LIFETIME value there (both gate views used to) walls an
 * entitled account at the free ceiling while the server is letting every create
 * through. A comp'd account is where that bites: it has no billing record for
 * the plan fallback to key off, so a features node missing the value has
 * nothing else left to resolve from.
 *
 * Any finite number stored under `key` IS the ceiling, including 0 and even a
 * negative, because that is what the server does with it: `features.get(key)`
 * returns the value, `if limit is None` does not fire, and `count >= limit`
 * decides. `UserFeatures.__post_init__` permits 0 explicitly, so a stored 0 is
 * representable, and 0 or -1 blocks every create server-side. Treating either
 * as "unset" here (the `||` chain both views used to have) would hand the user
 * a create the backend deletes on arrival.
 *
 * What is NOT a ceiling is a value that is not a finite number at all: a
 * string, a NaN or a boolean hand-written into the node has no server
 * comparison worth mirroring (`count >= "many"` raises server-side), so the
 * client invents no limit from it and leaves the objection to the server.
 *
 * @param {object|null|undefined} userData the `users/{uid}` snapshot value
 * @param {"chatsPerMonth"|"lessonsPerMonth"} key the features/plan field to read
 * @returns {number|null} the ceiling, or null when the client must not block
 */
export function resolveMonthlyLimit(userData, key) {
  const isUsable = (value) =>
    typeof value === "number" && Number.isFinite(value);
  const featuresLimit = userData?.features?.[key];
  if (isUsable(featuresLimit)) return featuresLimit;
  // The plan fallback goes through getCurrentPlan, which honours billing.status
  // and so resolves a canceled row to the free plan (i.e. to no monthly value,
  // i.e. to "do not block"). checkPaidUserLimit reads PLAN_FEATURES_BY_LOOKUP_KEY
  // straight off billing.lookupKey without looking at status, so for an account
  // holding a features node with no monthly value AND a lapsed billing row the
  // server resolves that row's plan ceiling while this returns null. Left as is
  // deliberately: pricing.js documents getCurrentPlan as the single source of
  // truth for a user's plan and warns against reading the lookup key directly,
  // the population is accounts whose features node predates
  // jobs/backfillMonthlyFeatures.py, and the disagreement is client-permissive,
  // so the outcome is a create the server refuses rather than a user walled off
  // something they are entitled to.
  const planLimit = pricingStore.getCurrentPlan(userData?.billing)?.[key];
  return isUsable(planLimit) ? planLimit : null;
}

/**
 * Whether this account is on the free sample, for surfaces that SAY SO out loud.
 *
 * Both shipped free tests, ANDed, because they disagree at the edges and the
 * disagreement is not theoretical: `!features` is the server's own
 * `helpers/users.isFreeUser` (and what LessonsView's limit check uses), while
 * the billing-derived plan id is what AccountView and FreeChatView display, and
 * a lapsed subscription can leave one true and the other false. Anything that
 * announces "here is your free sample" or "that was your free sample" is wrong
 * in a way the user will notice if either derivation says the account is not
 * free, so the conservative direction is to stay quiet whenever either does.
 *
 * Lives here rather than in a component because three surfaces now depend on
 * exactly the same answer -- the usage pill, and the two sample-complete
 * screens -- and a fourth copy of the expression is how they start to drift.
 * Note this is NOT the gate a limit check may use: those must keep mirroring
 * the single server-side test their own ceiling comes from.
 *
 * The `features` half is `isServerFreeUser` above, so the absent-or-null test
 * and its own-property discipline have exactly one implementation: a comp'd
 * account holds a features node with no billing record, and this helper and the
 * gate predicate must not disagree about what that account is.
 *
 * @param {object|null|undefined} userData the `users/{uid}` snapshot value
 * @returns {boolean}
 */
export function isFreeTierAccount(userData) {
  const container = userData === null || typeof userData !== "object" ? {} : userData;
  return (
    isServerFreeUser(container) &&
    pricingStore.getCurrentPlan(container.billing).id === "free"
  );
}
